import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// ── Helper: normalise user object from any API response ──────────────────────
// The DB stores first_name + last_name separately.
// The backend may return either shape; we always produce a consistent object
// with a `fullName` field so the UI never has to guess.
function normaliseUser(raw) {
  if (!raw) return null;
  const firstName = raw.firstName || raw.first_name || '';
  const lastName  = raw.lastName  || raw.last_name  || '';
  const fullName  = raw.fullName  || `${firstName} ${lastName}`.trim() || raw.name || '';
  return { ...raw, fullName };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('adminUser');
    return u ? normaliseUser(JSON.parse(u)) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Refresh admin profile from DB on app load so name/role are always current
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    authAPI.getProfile()
      .then((res) => {
        const freshUser = normaliseUser(res.data);
        localStorage.setItem('adminUser', JSON.stringify(freshUser));
        setUser(freshUser);
      })
      .catch(() => {
        // Non-critical — keep using the cached user from localStorage
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.login(email, password);
      const { accessToken, user: userData } = res.data;
      const normalisedUser = normaliseUser(userData);
      localStorage.setItem('adminToken', accessToken);
      localStorage.setItem('adminUser', JSON.stringify(normalisedUser));
      setUser(normalisedUser);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Check credentials.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Logout API failure is non-critical; proceed with local cleanup
      console.warn('Logout API call failed:', err?.message);
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, login, logout, loading, error }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, error]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);